import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Input,
  Typography,
  Tabs,
  Statistic,
  Row,
  Col,
  Form,
  InputNumber,
  Switch,
  Select,
  App,
  Spin,
} from 'antd';
import {
  ReloadOutlined,
  SaveOutlined,
  StopOutlined,
  RedoOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  ReferralStatus,
  ReferralProgramConfig,
  ReferralConfigAudit,
  QualificationRunSummary,
  StaffReferralListItem,
  ReferralUserSummary,
  StaffQualificationLogEntry,
  REFERRAL_TERMINAL_STATUSES,
  RewardStatus,
} from '@mygate/common';
import { referralsService } from '@/services/referrals';
import { formatDateTime } from '@/utils/date';
import { ColumnsType } from 'antd/es/table';
import { UserDetailModal } from '@/components/UserDetailModal';

const { Text } = Typography;

export function Referrals() {
  const queryClient = useQueryClient();
  const { modal, message } = App.useApp();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [listPage, setListPage] = useState(1);
  const [logsPage, setLogsPage] = useState(1);
  const [limit] = useState(20);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const [configForm] = Form.useForm();

  const getUserLabel = (id: string, info?: ReferralUserSummary): string => {
    const fullName = info ? [info.firstName, info.lastName].filter(Boolean).join(' ').trim() : '';
    return fullName || info?.phoneNumber || id.slice(-8);
  };

  const renderUserLink = (id: string, info?: ReferralUserSummary) => (
    <Button
      type="link"
      size="small"
      icon={<UserOutlined />}
      onClick={() => setSelectedUserId(id)}
      style={{ padding: 0, height: 'auto' }}
    >
      {getUserLabel(id, info)}
    </Button>
  );

  const renderUserCell = (id: string, info?: ReferralUserSummary) => {
    const fullName = info ? [info.firstName, info.lastName].filter(Boolean).join(' ').trim() : '';
    return (
      <Space direction="vertical" size={0} style={{ lineHeight: 1.2 }}>
        {renderUserLink(id, info)}
        {info?.phoneNumber && fullName && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {info.phoneNumber}
          </Text>
        )}
      </Space>
    );
  };

  // Queries
  const { data: dashboardResponse, isLoading: dashboardLoading } = useQuery({
    queryKey: ['referrals-dashboard'],
    queryFn: () => referralsService.getDashboard(),
    refetchInterval: 30000,
  });

  const { data: listResponse, isLoading: listLoading } = useQuery({
    queryKey: ['referrals-list', listPage, limit, statusFilter],
    queryFn: () => referralsService.getList({ page: listPage, limit, status: statusFilter }),
  });

  const { data: configResponse, isLoading: configLoading } = useQuery({
    queryKey: ['referrals-config'],
    queryFn: () => referralsService.getConfig(),
  });

  const { data: auditResponse, isLoading: auditLoading } = useQuery({
    queryKey: ['referrals-config-audit'],
    queryFn: () => referralsService.getConfigAudit(),
    enabled: activeTab === 'config',
  });

  const { data: logsResponse, isLoading: logsLoading } = useQuery({
    queryKey: ['referrals-qualification-logs', logsPage, limit],
    queryFn: () => referralsService.getQualificationLogs({ page: logsPage, limit }),
    enabled: activeTab === 'logs',
  });

  const dashboard = dashboardResponse?.data;
  const referrals = listResponse?.data?.referrals || [];
  const totalReferrals = listResponse?.data?.total || 0;
  const config = configResponse?.data;
  const auditLogs = auditResponse?.data?.audits || [];
  const qualificationLogs = logsResponse?.data?.runs || [];
  const totalLogs = logsResponse?.data?.total || 0;

  // Mutations
  const updateConfigMutation = useMutation({
    mutationFn: (data: ReferralProgramConfig) => referralsService.updateConfig(data),
    onSuccess: () => {
      message.success('Configuration updated successfully');
      queryClient.invalidateQueries({ queryKey: ['referrals-config'] });
      queryClient.invalidateQueries({ queryKey: ['referrals-config-audit'] });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update configuration';
      message.error(errorMessage);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => referralsService.rejectReferral(id, reason),
    onSuccess: () => {
      message.success('Referral rejected');
      queryClient.invalidateQueries({ queryKey: ['referrals-list'] });
      queryClient.invalidateQueries({ queryKey: ['referrals-dashboard'] });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reject referral';
      message.error(errorMessage);
    },
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => referralsService.retryDistribution(id),
    onSuccess: () => {
      message.success('Distribution retry initiated');
      queryClient.invalidateQueries({ queryKey: ['referrals-list'] });
      queryClient.invalidateQueries({ queryKey: ['referrals-dashboard'] });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to retry distribution';
      message.error(errorMessage);
    },
  });

  const forceQualifyMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => referralsService.forceQualify(id, reason),
    onSuccess: response => {
      const distSucceeded = response?.data?.distribution?.success;
      if (distSucceeded) {
        message.success('Referral qualified and rewards distributed');
      } else {
        const err = response?.data?.distribution?.error;
        message.warning(`Referral qualified, distribution incomplete${err ? `: ${err}` : ''}`);
      }
      queryClient.invalidateQueries({ queryKey: ['referrals-list'] });
      queryClient.invalidateQueries({ queryKey: ['referrals-dashboard'] });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to force-qualify referral';
      message.error(errorMessage);
    },
  });

  const runLogsMutation = useMutation({
    mutationFn: (runId: string) => referralsService.getRunLogs(runId),
  });

  // Handlers
  const handleSaveConfig = async () => {
    try {
      const values = await configForm.validateFields();
      updateConfigMutation.mutate(values);
    } catch {
      message.error('Please fix form errors');
    }
  };

  const handleReject = (id: string) => {
    let rejectReason = '';
    modal.confirm({
      title: 'Reject Referral',
      content: (
        <div>
          <p>Are you sure you want to reject this referral?</p>
          <Input
            placeholder="Rejection reason (optional)"
            onChange={e => (rejectReason = e.target.value)}
            style={{ marginTop: 10 }}
          />
        </div>
      ),
      onOk: () => {
        rejectMutation.mutate({ id, reason: rejectReason.trim() || undefined });
      },
    });
  };

  const handleRetry = (id: string) => {
    modal.confirm({
      title: 'Retry Distribution',
      content: 'Are you sure you want to retry reward distribution for this referral?',
      onOk: () => {
        retryMutation.mutate(id);
      },
    });
  };

  const handleForceQualify = (id: string, refereeLabel?: string) => {
    let reasonInput = '';
    modal.confirm({
      title: 'Force Qualify Referral',
      width: 520,
      content: (
        <div>
          <p>
            Bypass automated checks and mark this referral as <Tag color="green">QUALIFIED</Tag>. Rewards will be
            created and distributed to the referrer and referee accounts immediately.
          </p>
          {refereeLabel && (
            <p>
              <Text type="secondary">Referee: </Text>
              <Text strong>{refereeLabel}</Text>
            </p>
          )}
          <Input.TextArea
            placeholder="Reason (optional, recorded in audit log)"
            rows={3}
            onChange={e => (reasonInput = e.target.value)}
            style={{ marginTop: 10 }}
          />
        </div>
      ),
      okText: 'Force Qualify',
      okButtonProps: { type: 'primary' },
      onOk: () => {
        forceQualifyMutation.mutate({ id, reason: reasonInput.trim() || undefined });
      },
    });
  };

  const handleExpandRow = async (runId: string) => {
    if (expandedRowKeys.includes(runId)) {
      setExpandedRowKeys(expandedRowKeys.filter(key => key !== runId));
    } else {
      setExpandedRowKeys([...expandedRowKeys, runId]);
      await runLogsMutation.mutateAsync(runId);
    }
  };

  // Status tag color mapping
  const getStatusColor = (status: ReferralStatus) => {
    switch (status) {
      case ReferralStatus.SIGNED_UP:
        return 'blue';
      case ReferralStatus.KYC_PENDING:
        return 'orange';
      case ReferralStatus.KYC_COMPLETED:
        return 'cyan';
      case ReferralStatus.QUALIFYING:
        return 'gold';
      case ReferralStatus.QUALIFIED:
        return 'green';
      case ReferralStatus.REWARDED:
        return 'green';
      case ReferralStatus.REJECTED:
        return 'red';
      case ReferralStatus.EXPIRED:
        return 'default';
      default:
        return 'default';
    }
  };

  // Referral list columns
  const referralColumns: ColumnsType<StaffReferralListItem> = [
    {
      title: 'Referrer',
      dataIndex: 'referrerId',
      key: 'referrerId',
      width: 200,
      render: (id: string, record) => renderUserCell(id, record.referrerInfo),
    },
    {
      title: 'Referee',
      dataIndex: 'refereeId',
      key: 'refereeId',
      width: 200,
      render: (id: string, record) => renderUserCell(id, record.refereeInfo),
    },
    {
      title: 'Empfehlung',
      key: 'referral',
      width: 160,
      render: (_, record) => (
        <div className="table-cell-stack">
          <Tag>{record.referralCode}</Tag>
          <Text className="table-cell-muted" copyable={{ text: record.id }}>
            {record.id.slice(-8)}
          </Text>
          <Text className="table-cell-muted">{formatDateTime(record.createdAt)}</Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: ReferralStatus) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: 'Qualifizierung',
      key: 'qualification',
      width: 190,
      render: (_, record) => (
        <div className="table-cell-stack">
          <Tag color={record.qualificationStatus.kycCompleted ? 'green' : 'default'}>
            KYC: {record.qualificationStatus.kycCompleted ? 'Ja' : 'Nein'}
          </Tag>
          <Tag color={record.qualificationStatus.accountFunded ? 'green' : 'default'}>
            Einzahlung: {record.qualificationStatus.accountFunded ? 'Ja' : 'Nein'}
          </Tag>
          <Text className="table-cell-muted">Transaktionen: {record.qualificationStatus.transactionCount}</Text>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => {
        const isTerminal = REFERRAL_TERMINAL_STATUSES.includes(
          record.status as (typeof REFERRAL_TERMINAL_STATUSES)[number],
        );
        const isFailed =
          record.referrerRewardStatus === RewardStatus.FAILED || record.refereeRewardStatus === RewardStatus.FAILED;
        const refereeLabel = getUserLabel(record.refereeId, record.refereeInfo);

        return (
          <Space size="small" wrap>
            {!isTerminal && (
              <>
                <Button
                  size="small"
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={() => handleForceQualify(record.id, refereeLabel)}
                  loading={forceQualifyMutation.isPending}
                >
                  Force Qualify
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<StopOutlined />}
                  onClick={() => handleReject(record.id)}
                  loading={rejectMutation.isPending}
                >
                  Reject
                </Button>
              </>
            )}
            {isFailed && (
              <Button
                size="small"
                icon={<RedoOutlined />}
                onClick={() => handleRetry(record.id)}
                loading={retryMutation.isPending}
              >
                Retry
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  // Qualification logs columns
  const logColumns: ColumnsType<QualificationRunSummary> = [
    {
      title: 'Run ID',
      dataIndex: 'runId',
      key: 'runId',
      width: 200,
      ellipsis: true,
      render: (runId: string) => <Text copyable>{runId}</Text>,
    },
    {
      title: 'Run Date',
      dataIndex: 'runAt',
      key: 'runAt',
      width: 150,
      render: (date: Date) => formatDateTime(date),
    },
    {
      title: 'Total Checked',
      dataIndex: 'totalChecked',
      key: 'totalChecked',
      width: 120,
    },
    {
      title: 'Qualified',
      dataIndex: 'qualified',
      key: 'qualified',
      width: 100,
      render: (count: number) => <Tag color="green">{count}</Tag>,
    },
    {
      title: 'Failed',
      dataIndex: 'failed',
      key: 'failed',
      width: 100,
      render: (count: number) => (count > 0 ? <Tag color="red">{count}</Tag> : <Tag>{count}</Tag>),
    },
    {
      title: 'Errors',
      dataIndex: 'errors',
      key: 'errors',
      width: 100,
      render: (count: number) => (count > 0 ? <Tag color="red">{count}</Tag> : <Tag>{count}</Tag>),
    },
  ];

  // Audit log columns
  const auditColumns: ColumnsType<ReferralConfigAudit> = [
    {
      title: 'Staff ID',
      dataIndex: 'staffId',
      key: 'staffId',
      width: 150,
      ellipsis: true,
      render: (id: string) => <Text copyable>{id.slice(-8)}</Text>,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (action: string) => <Tag>{action}</Tag>,
    },
    {
      title: 'Changed At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: Date) => formatDateTime(date),
    },
    {
      title: 'Previous Value',
      dataIndex: 'previousValue',
      key: 'previousValue',
      width: 200,
      ellipsis: true,
      render: (value: Partial<ReferralProgramConfig>) => <Text code>{JSON.stringify(value)}</Text>,
    },
    {
      title: 'New Value',
      dataIndex: 'newValue',
      key: 'newValue',
      width: 200,
      ellipsis: true,
      render: (value: Partial<ReferralProgramConfig>) => <Text code>{JSON.stringify(value)}</Text>,
    },
  ];

  const renderCheckTag = (
    label: string,
    check: { checked: boolean; result: boolean | number; details?: string; depositCount?: number; required?: number },
  ) => {
    if (!check?.checked) {
      return <Tag>{label}: —</Tag>;
    }
    if (typeof check.result === 'boolean') {
      return (
        <Tag
          color={check.result ? 'green' : 'red'}
          icon={check.result ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          title={check.details}
        >
          {label}: {check.result ? '✓' : '✗'}
        </Tag>
      );
    }
    const required = check.required;
    const passed = required != null ? check.result >= required : check.result > 0;
    const value = required != null ? `${check.result}/${required}` : String(check.result);
    return (
      <Tag
        color={passed ? 'green' : 'red'}
        icon={passed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        title={check.details}
      >
        {label}: {value}
      </Tag>
    );
  };

  const expandedRowRender = (record: QualificationRunSummary) => {
    const runDetails = runLogsMutation.data?.data;
    if (!runDetails || runDetails.runId !== record.runId) {
      return <Spin size="small" />;
    }
    if (runDetails.logs.length === 0) {
      return <Text type="secondary">No log entries for this run.</Text>;
    }

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {runDetails.logs.map((log: StaffQualificationLogEntry) => {
          const refereeLabel = getUserLabel(log.refereeId, log.refereeInfo);
          const isTerminal = REFERRAL_TERMINAL_STATUSES.includes(
            log.newStatus as (typeof REFERRAL_TERMINAL_STATUSES)[number],
          );
          return (
            <Card key={log.id || `${log.referralId}-${log.runId}`} size="small">
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <Space wrap>
                  <Tag color={log.qualified ? 'green' : log.error ? 'red' : 'default'}>
                    {log.qualified ? 'QUALIFIED' : log.error ? 'ERROR' : 'NOT QUALIFIED'}
                  </Tag>
                  {log.statusChanged && (
                    <Tag color="blue">
                      {log.previousStatus} → {log.newStatus}
                    </Tag>
                  )}
                  {log.rewardCreated && <Tag color="purple">Reward Created</Tag>}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Referral: <Text copyable={{ text: log.referralId }}>{log.referralId.slice(-8)}</Text>
                  </Text>
                </Space>

                <Space wrap>
                  <Text type="secondary">Referee:</Text>
                  {renderUserLink(log.refereeId, log.refereeInfo)}
                </Space>

                <Space wrap>
                  {renderCheckTag('KYC', log.checks.kyc)}
                  {renderCheckTag('Funded', log.checks.accountFunded)}
                  {renderCheckTag('Txns', log.checks.transactionCount)}
                </Space>

                {log.error && (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {log.error}
                  </Text>
                )}

                {log.distributionResult?.error && (
                  <Text type="warning" style={{ fontSize: 12 }}>
                    Distribution: {log.distributionResult.error}
                  </Text>
                )}

                {!isTerminal && !log.qualified && (
                  <div>
                    <Button
                      size="small"
                      type="primary"
                      icon={<ThunderboltOutlined />}
                      onClick={() => handleForceQualify(log.referralId, refereeLabel)}
                      loading={forceQualifyMutation.isPending}
                    >
                      Force Qualify
                    </Button>
                  </div>
                )}
              </Space>
            </Card>
          );
        })}
      </Space>
    );
  };

  return (
    <>
      <div className="page-toolbar">
        <Typography.Text type="secondary">Empfehlungen, Prämien und Qualifizierung im Blick.</Typography.Text>
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['referrals-dashboard'] });
              queryClient.invalidateQueries({ queryKey: ['referrals-list'] });
              queryClient.invalidateQueries({ queryKey: ['referrals-config'] });
              queryClient.invalidateQueries({ queryKey: ['referrals-qualification-logs'] });
            }}
          >
            Refresh
          </Button>
        </Space>
      </div>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="Dashboard" key="dashboard">
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Row className="operational-stats" gutter={[16, 16]}>
                <Col xs={24} md={12} xl={6}>
                  <Card>
                    <Statistic
                      title="Codes Generated"
                      value={dashboard?.codesGenerated || 0}
                      loading={dashboardLoading}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={12} xl={6}>
                  <Card>
                    <Statistic
                      title="Total Referrals"
                      value={dashboard?.totalReferrals || 0}
                      loading={dashboardLoading}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={12} xl={6}>
                  <Card>
                    <Statistic
                      title="Qualified"
                      value={dashboard?.qualifiedReferrals || 0}
                      loading={dashboardLoading}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={12} xl={6}>
                  <Card>
                    <Statistic title="Pending" value={dashboard?.pendingReferrals || 0} loading={dashboardLoading} />
                  </Card>
                </Col>
              </Row>
              <Row className="operational-stats" gutter={[16, 16]}>
                <Col xs={24} md={12} xl={8}>
                  <Card>
                    <Statistic title="Rejected" value={dashboard?.rejectedReferrals || 0} loading={dashboardLoading} />
                  </Card>
                </Col>
                <Col xs={24} md={12} xl={8}>
                  <Card>
                    <Statistic
                      title="Total EUR Distributed"
                      value={dashboard?.totalRewardsDistributed || 0}
                      precision={2}
                      prefix="€"
                      loading={dashboardLoading}
                    />
                  </Card>
                </Col>
                <Col xs={24} md={12} xl={8}>
                  <Card>
                    <Statistic
                      title="Conversion Rate"
                      value={dashboard?.conversionRate || 0}
                      precision={1}
                      suffix="%"
                      loading={dashboardLoading}
                    />
                  </Card>
                </Col>
              </Row>
            </Space>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Referral List" key="list">
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12} xl={8}>
                  <Select
                    placeholder="Filter by status"
                    style={{ width: '100%' }}
                    onChange={setStatusFilter}
                    value={statusFilter}
                    allowClear
                  >
                    {Object.values(ReferralStatus).map(status => (
                      <Select.Option key={status} value={status}>
                        {status}
                      </Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>

              <Table
                columns={referralColumns}
                dataSource={referrals}
                rowKey="id"
                loading={listLoading}
                pagination={{
                  current: listPage,
                  pageSize: limit,
                  total: totalReferrals,
                  onChange: newPage => setListPage(newPage),
                  showSizeChanger: false,
                  showTotal: total => `Total ${total} referrals`,
                }}
                scroll={{ x: 1080 }}
              />
            </Space>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Qualification Logs" key="logs">
            <Table
              scroll={{ x: 'max-content' }}
              columns={logColumns}
              dataSource={qualificationLogs}
              rowKey="runId"
              loading={logsLoading}
              expandable={{
                expandedRowRender,
                expandedRowKeys,
                onExpand: (expanded, record) => {
                  if (expanded) {
                    handleExpandRow(record.runId);
                  } else {
                    setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.runId));
                  }
                },
              }}
              pagination={{
                current: logsPage,
                pageSize: limit,
                total: totalLogs,
                onChange: newPage => setLogsPage(newPage),
                showSizeChanger: false,
                showTotal: total => `Total ${total} runs`,
              }}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Configuration" key="config">
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Card title="Program Settings" loading={configLoading}>
                <Form
                  form={configForm}
                  layout="vertical"
                  initialValues={config}
                  onFinish={handleSaveConfig}
                  key={JSON.stringify(config)}
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Program Enabled"
                        name="programEnabled"
                        valuePropName="checked"
                        rules={[{ required: true }]}
                      >
                        <Switch />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item
                        label="Referrer Reward (EUR)"
                        name="referrerReward"
                        rules={[{ required: true, type: 'number', min: 0 }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item
                        label="Referee Reward (EUR)"
                        name="refereeReward"
                        rules={[{ required: true, type: 'number', min: 0 }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item
                        label="Min Transactions"
                        name="minTransactionsRequired"
                        rules={[{ required: true, type: 'number', min: 1 }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={1} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item
                        label="Attribution Window (Days)"
                        name="attributionWindowDays"
                        rules={[{ required: true, type: 'number', min: 1 }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={1} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12} xl={8}>
                      <Form.Item
                        label="Max Referrals Per User"
                        name="maxReferralsPerUser"
                        rules={[{ type: 'number', min: 0 }]}
                      >
                        <InputNumber style={{ width: '100%' }} min={0} placeholder="Unlimited" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={updateConfigMutation.isPending}
                    >
                      Save Configuration
                    </Button>
                  </Form.Item>
                </Form>
              </Card>

              <Card title="Configuration Change History">
                <Table
                  scroll={{ x: 'max-content' }}
                  columns={auditColumns}
                  dataSource={auditLogs}
                  rowKey={(record, index) => `${record.createdAt}-${index}`}
                  loading={auditLoading}
                  pagination={{ pageSize: 10, showTotal: total => `Total ${total} changes` }}
                />
              </Card>
            </Space>
          </Tabs.TabPane>
        </Tabs>
      </Card>

      <UserDetailModal
        userId={selectedUserId}
        visible={selectedUserId !== null}
        onClose={() => setSelectedUserId(null)}
      />
    </>
  );
}
